import {afterNextRender, DestroyRef, inject, Injectable, Injector, makeStateKey, PLATFORM_ID, TransferState} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpContext, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {defer, EMPTY, Observable, Subject} from 'rxjs';
import {catchError, finalize, takeUntil, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RESPONSE} from '../providers/http-context-providers';
import {RESOURCE_PAGE_REQUEST} from '../providers/http-context-tokens';
import {ResponseErrorsHandlerService} from './response-errors-handler.service';
import {SeoService} from './seo.service';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import type {Response} from 'express';

export type ResourcePageStatus = '404' | '403' | '500' | 'retry';
export interface ResourcePageRequestOptions {context: HttpContext; headers: HttpHeaders;}
interface TransferredFailure {status: number; primary: boolean; path: string | null;}

/** Provide on a page component, never at the application root. */
@Injectable()
export class ResourcePageState {
  private readonly platform = inject(PLATFORM_ID);
  private readonly transfer = inject(TransferState);
  private readonly response = inject<Response | null>(RESPONSE, {optional: true});
  private readonly errors = inject(ResponseErrorsHandlerService);
  private readonly seo = inject(SeoService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private identity = '';
  private revision = 0;
  private hasContent = false;
  private forceRefresh = false;
  private destroyed = false;
  private readonly superseded = new Subject<void>();
  readonly retry$ = new Subject<void>();
  status: ResourcePageStatus | null = null;
  loading = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.retry$.complete();
      this.superseded.complete();
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.status) this.renderStatus(this.status);
    });
  }

  canRetain(identity: string): boolean {
    return this.identity === identity && this.hasContent;
  }

  reset(): void {
    this.superseded.next();
    ++this.revision;
    this.identity = '';
    this.hasContent = false;
    this.forceRefresh = false;
    this.status = null;
    this.loading = false;
  }

  retry(): void {
    this.forceRefresh = true;
    this.retry$.next();
  }

  load<T>(identity: string, request: (options: ResourcePageRequestOptions) => Observable<T>, hideForbidden = false): Observable<T> {
    return defer(() => {
      this.superseded.next();
      const revision = ++this.revision;
      if (this.identity !== identity) {
        this.identity = identity;
        this.hasContent = false;
      }
      this.status = null;
      this.loading = true;
      const pageUrl = (this.router.getCurrentNavigation()?.extractedUrl?.toString() || this.router.url).split(/[?#]/)[0];
      const key = makeStateKey<TransferredFailure | null>('resource-page:' + pageUrl + ':' + identity);
      const restored = this.transfer.get(key, null);
      this.transfer.remove(key);
      if (isPlatformBrowser(this.platform) && restored && !this.forceRefresh) {
        this.loading = false;
        this.fail(restored.status, restored.primary, hideForbidden);
        if (restored.status === 0 || restored.status >= 500) {
          afterNextRender(() => {
            if (!this.destroyed && revision === this.revision) {
              this.errors.handleServerErrors(new HttpErrorResponse({status: restored.status, url: restored.path || undefined}), 'GET');
            }
          }, {injector: this.injector});
        }
        return EMPTY;
      }
      let primaryError: unknown;
      const options: ResourcePageRequestOptions = {
        context: new HttpContext().set(RESOURCE_PAGE_REQUEST, error => primaryError = error),
        headers: this.forceRefresh || this.hasContent ? new HttpHeaders({'x-cache-refresh': '1'}) : new HttpHeaders(),
      };
      this.forceRefresh = false;
      return request(options).pipe(
        tap(() => {
          if (revision !== this.revision) return;
          this.hasContent = true;
          this.status = null;
          const wasError = this.seo.pageError;
          this.seo.clearPageError();
          if (wasError) {
            this.seo.setTitle('Watch This Garage', '');
            this.seo.setDescription(this.translate.instant('meta.description'));
            this.seo.setCanonical(this.router.url);
            this.seo.setLocaleAlternates(this.router.url);
          }
          if (this.response && !this.response.headersSent) this.response.status(200);
        }),
        catchError((error: unknown) => {
          if (revision !== this.revision) return EMPTY;
          const status = error instanceof HttpErrorResponse ? error.status : 503;
          const primary = error === primaryError;
          this.fail(status, primary, hideForbidden);
          if (!isPlatformBrowser(this.platform)) {
            const path = error instanceof HttpErrorResponse && error.url
              ? new URL(error.url, 'http://localhost').pathname : null;
            this.transfer.set(key, {status, primary, path});
          }
          return EMPTY;
        }),
        finalize(() => { if (revision === this.revision) this.loading = false; }),
        takeUntil(this.superseded),
        takeUntilDestroyed(this.destroyRef),
      );
    });
  }

  show(status: ResourcePageStatus): void {
    this.superseded.next();
    ++this.revision;
    this.renderStatus(status);
  }

  private renderStatus(status: ResourcePageStatus): void {
    this.status = status;
    this.loading = false;
    this.seo.setPageError(this.translate.instant(`shared.resource_page.${status}.title`),
      this.translate.instant(`shared.resource_page.${status}.description`));
    if (this.response && !this.response.headersSent) {
      this.response.status(status === 'retry' ? 503 : Number(status));
      this.response.setHeader('Cache-Control', 'no-store');
    }
  }

  private fail(status: number, primary: boolean, hideForbidden: boolean): void {
    if (primary && (status === 404 || status === 403)) {
      this.hasContent = false;
      this.renderStatus(status === 404 || hideForbidden ? '404' : '403');
    } else if (!this.hasContent) {
      this.renderStatus('retry');
    }
  }
}
